import { useState } from 'react';
import { extractValue } from '../../../../util/formatting';

/**
 * Handles the "back" navigation for bulk-import orders on the payment step.
 * Fetches file failure data and rebuilds the table rows for the fix-errors step.
 */
export function useBulkImportBack(props: {
  fileIdInfo?: string | null;
  setActiveStep?: (step: number) => void;
  setFailedData?: (val: string) => void;
  setSuccessData?: (val: string) => void;
  setFixErrorTableData?: (val: string) => void;
  setFileState?: (val: boolean) => void;
  setTableData?: (val: string) => void;
}) {
  const [isBackLoading, setIsBackLoading] = useState(false);

  async function fixIssues() {
    if (!props.fileIdInfo) return;
    setIsBackLoading(true);
    try {
      // Dynamic import to avoid circular dependencies
      const { getFileFailureDataAPI } = await import(
        '../../../properties/bulk-import/service/apiService'
      );
      const fileData = await getFileFailureDataAPI(props.fileIdInfo, false);
      if (fileData) processTableData(fileData.data);
    } catch {
      setIsBackLoading(false);
    }
  }

  function processTableData(tableData: Array<Record<string, unknown>>) {
    const result: Array<Record<string, unknown>> = [];
    let failedCount = 0;
    let successCount = 0;

    tableData.forEach((item, index) => {
      const row = buildRow(item, index);
      if (item.errorReasons) failedCount++;
      else successCount++;
      result.push(row);
    });

    props.setSuccessData?.(successCount.toString());
    props.setFailedData?.(failedCount.toString());
    props.setFileState?.(false);
    props.setFixErrorTableData?.(JSON.stringify(result));
    props.setTableData?.(JSON.stringify(result));
    setIsBackLoading(false);
    props.setActiveStep?.(2);
  }

  return { isBackLoading, fixIssues };
}

// ---------------------------------------------------------------------------
// Row builder
// ---------------------------------------------------------------------------

function makeField(val: unknown, opt = false) {
  return {
    value: extractValue(val as string | null),
    isActive: false,
    ...(opt ? { isOptionalField: true } : {}),
  };
}

type FieldEntry = { value: unknown; isActive: boolean; errorMessage?: string };

function buildRow(
  item: Record<string, unknown>,
  index: number
): Record<string, unknown> {
  const services = Array.isArray(item.services)
    ? { value: (item.services as string[]).map(extractValue), isActive: false }
    : makeField(item.services);
  const address = makeField(item.address);
  const propertyType = makeField(item.propertyType);
  const unitNumber = makeField(item.unitNumber);
  const city = makeField(item.city);
  const state = makeField(item.state);
  const zipCode = makeField(item.zip);
  const onsiteContactName = makeField(item.onsiteContactName);
  const onsiteContactPhone = makeField(item.onsiteContactPhone);
  const accessType = makeField(item.accessType);
  const lockboxLocation = makeField(item.lockboxLocation);
  const accessCode = makeField(item.accessCode);
  const rentlyRegistrationPin = item.rentlyRegistrationPin
    ? makeField(item.rentlyRegistrationPin)
    : { value: '-', isActive: false };
  const entity = makeField(item.entity);
  const orderNotes = makeField(item.orderNotes, true);
  const gateCode = makeField(item.gateCode, true);
  const firstAvailableDate = makeField(item.firstAvailableDate, true);
  const propertyId = makeField(item.propertyId, true);
  const beds = makeField(item.beds, true);
  const baths = makeField(item.baths, true);
  const squareFeet = makeField(item.squareFeet, true);

  if (item.errorReasons) {
    applyErrorReasons(item.errorReasons as string[], {
      services, address, propertyType, unitNumber, city, state, zipCode,
      onsiteContactName, onsiteContactPhone, accessType, lockboxLocation,
      accessCode, rentlyRegistrationPin, orderNotes, gateCode,
      firstAvailableDate, propertyId, beds, baths, squareFeet,
    });
  }

  return {
    id: index + 1, fileId: item.fileId, status: item.status,
    orderId: item.orderId, propertyPrice: item.propertyPrice,
    orderSource: item.orderSource, rowNumber: item.rowNumber,
    services, propertyType, address, unitNumber, city, state, zipCode,
    onsiteContactName, onsiteContactPhone, accessType, lockboxLocation,
    accessCode, rentlyRegistrationPin, entity, orderNotes, gateCode,
    firstAvailableDate, propertyId, beds, baths, squareFeet,
  };
}

const ERROR_MAP: Array<[string[], string, string]> = [
  [['services', 'Service(s)'], 'services', 'Not a valid service.'],
  [['Address', 'address1'], 'address', 'Not a valid address.'],
  [['Property Type'], 'propertyType', 'Not a valid property Type.'],
  [['City'], 'city', 'Not a valid city.'],
  [['State', 'state'], 'state', 'Not a valid state.'],
  [['zip code', 'zip', 'zipcode', 'Zip Code', 'Zip'], 'zipCode', 'Not a valid zip code.'],
  [['On-Site Contact Name'], 'onsiteContactName', 'Not a valid on-site contact name.'],
  [['On-Site Contact Phone'], 'onsiteContactPhone', 'Not a valid on-site contact phone.'],
  [['Access Type'], 'accessType', 'Not a valid access type.'],
  [['Lockbox Location'], 'lockboxLocation', 'Not a valid Lockbox location.'],
  [['accessCode', 'Access Code'], 'accessCode', 'Not a valid access code.'],
  [['Property Id', 'Property ID'], 'propertyId', 'Limit 50 characters'],
  [['rentlyRegistrationPin', 'Rently Registration Pin'], 'rentlyRegistrationPin', 'Not a valid rently registration pin.'],
  [['orderNotes', 'Order Notes'], 'orderNotes', 'Not a valid notes'],
  [['gateCode', 'Gate code', 'Gate Code'], 'gateCode', 'Not a valid gate code'],
  [['firstAvailableDate', 'Schedule Later Date', 'First Available Date', 'First available date'], 'firstAvailableDate', 'Enter a valid date'],
  [['Bedrooms', 'Beds'], 'beds', 'Enter a valid number'],
  [['Bathrooms', 'Baths'], 'baths', 'Enter a valid number'],
  [['Square Feet'], 'squareFeet', 'Enter a valid number'],
];

function applyErrorReasons(
  reasons: string[],
  fields: Record<string, FieldEntry>
) {
  let anyActive = false;
  for (const reason of reasons) {
    for (const [keywords, fieldKey, msg] of ERROR_MAP) {
      if (keywords.some((k) => reason.includes(k)) && fields[fieldKey]) {
        fields[fieldKey].isActive = true;
        fields[fieldKey].errorMessage = msg;
        anyActive = true;
      }
    }
    if (reason.includes('Something went wrong, please contact admin') && !anyActive) {
      const allFields = [
        'services', 'address', 'propertyType', 'city', 'unitNumber', 'state',
        'zipCode', 'onsiteContactName', 'onsiteContactPhone', 'accessType',
        'lockboxLocation', 'accessCode', 'rentlyRegistrationPin',
      ];
      for (const f of allFields) {
        if (fields[f]) {
          fields[f].isActive = true;
          fields[f].errorMessage = `Not a valid ${f}.`;
        }
      }
    }
  }
}
